import { useEffect, useState } from 'react';

import { revokeRoleGroupFromUser, useFetchUserRoleGroupAssignments } from '@jobandtalent/auth-api-client-v2';
import { Button } from '@jobandtalent/design-system';

import Privilege from 'src/components/Privilege/Privilege';
import Section from 'src/components/Section/Section';
import useError, { DEFAULT_ERROR } from 'src/hooks/useError/useError';
import useLocalPermissions from 'src/hooks/useLocalPermissions/useLocalPermissions';
import useNotification, { DEFAULT_SUCCESS } from 'src/hooks/useNotification/useNotification';

import NewRoleGroupForm from '../NewRoleGroupForm/NewRoleGroupForm';
import { useUserState } from '../UserState';

import styles from './EditUserForm.module.scss';

const UserRoleGroupsSection = () => {
  const { state } = useUserState();
  const { error, setError } = useError();
  const { notification, setNotification } = useNotification();
  const { permissions, error: permissionError } = useLocalPermissions();
  const [showNewRoleGroupForm, setShowNewRoleGroupForm] = useState(false);
  const {
    data: roleGroupsData,
    error: roleGroupsError,
    mutate: roleGroupsMutate
  } = useFetchUserRoleGroupAssignments(state.user?.id as number);

  useEffect(() => {
    if (roleGroupsError) {
      setError(roleGroupsError?.response?.data ? roleGroupsError.response.data : DEFAULT_ERROR);
    }
  }, [roleGroupsError]);

  const serviceUser = state.user?.service_user;
  const serviceUsersPermission = !permissionError && permissions.serviceUsersPermission;
  const canManageRoleGroups = !serviceUser || serviceUsersPermission;

  const onClickRevokeRoleGroup = async (roleGroupId: number) => {
    if (!state.user) {
      return;
    }

    revokeRoleGroupFromUser(state.user.id, roleGroupId).then(
      () => roleGroupsMutate(),
      (requestError) => {
        setError(requestError?.response?.data ? requestError.response.data : DEFAULT_ERROR);
      }
    );
  };

  const onSuccessNewRoleGroupForm = () => {
    roleGroupsMutate();
    setNotification(DEFAULT_SUCCESS);
    setShowNewRoleGroupForm(false);
  };

  return (
    <>
      <Section icon="person_accepted" title="Roles Groups">
        {roleGroupsData?.data.user_role_groups?.map((roleGroup) => (
          <Privilege
            key={roleGroup.id}
            title={roleGroup.role_group_name}
            onRevoke={canManageRoleGroups ? () => onClickRevokeRoleGroup(roleGroup.role_group_id) : undefined}
          />
        ))}
        <Button
          className={styles.grantPrivilege}
          dataAttributes={{ testid: 'grantRoleGroupButton' }}
          disabled={!canManageRoleGroups}
          onSelect={() => setShowNewRoleGroupForm(true)}
          secondary
          tiny
        >
          Grant role group
        </Button>
      </Section>
      {showNewRoleGroupForm && state.user && (
        <NewRoleGroupForm
          onClose={() => setShowNewRoleGroupForm(false)}
          onSuccess={onSuccessNewRoleGroupForm}
          selectedUser={state.user}
        />
      )}
      {error}
      {notification}
    </>
  );
};

export default UserRoleGroupsSection;
